import React from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@ayble-health/dashboard-components';
import { Chip, type ChipVariant } from 'components/ui/chip';
import type { VisitStatus } from '../../models/providerHome';

const fmtSlot = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * Visit status, as a pill.
 *
 * The chrome is `Chip` — this file no longer draws pills. It used to hand-roll
 * seven `Box`es with borders and fills that duplicated Chip's vocabulary
 * exactly, which is why the design system documented two pill sets that were
 * the same object under different names. `aqua`, `symptom` and `alert` now carry
 * all of it; `alert` was added to Chip for the four statuses that had no
 * equivalent there.
 *
 * What this component still owns, and why it is not just a Chip variant map:
 *
 * - **`occurred` has no pill.** It is bare muted text. A Chip variant cannot
 *   render no container — that is an anatomy change for one value, not a
 *   configuration of the others.
 * - **`unconfirmed` is a control.** Clicking it confirms the appointment, so it
 *   carries a pointer cursor and a title. Chip is deliberately inert
 *   (`cursor: default`, `user-select: none`); the interactive pill is
 *   `PressablePill`, which is a different component for exactly this reason.
 * - **`rescheduled` composes.** It appends a formatted slot time inside the
 *   pill, so the label is two nodes rather than a string.
 *
 * Those three are why the status→chrome mapping lives here rather than at the
 * call sites: two of them are behaviour, and behaviour is not skinnable.
 */

/**
 * `md` is the original size, used by the provider Home's visit rows. `sm` is
 * for tight tabular contexts — the Care roster fits the pill on the same line
 * as an appointment's date and time, where the full-size pill wraps.
 *
 * `minH: 0` defeats the 20px floor Chakra's Tag slot recipe puts on the Chip
 * root; without it `py` cannot make the pill shorter and both sizes collapse to
 * the same height.
 */
const SIZES = {
  md: { px: 3, py: '3px', fontSize: 'xs' },
  sm: { px: 2, py: '1px', fontSize: '2xs' },
} as const;

export type StatusPillSize = keyof typeof SIZES;

/** The whole status vocabulary, as Chip variants plus a label. */
const STATUS_CHIP: Partial<Record<VisitStatus, { variant: ChipVariant; label: string }>> = {
  confirmed: { variant: 'aqua', label: 'Confirmed' },
  unconfirmed: { variant: 'symptom', label: 'Unconfirmed' },
  rescheduled: { variant: 'alert', label: 'Rescheduled' },
  late_cancel: { variant: 'alert', label: 'Late Cancel' },
  no_show: { variant: 'alert', label: 'No Showed' },
  cancelled: { variant: 'alert', label: 'Cancelled' },
};

type Props = {
  status: VisitStatus;
  rescheduledTo?: string;
  onConfirm?: () => void;
  size?: StatusPillSize;
};

export const StatusPill = ({
  status,
  rescheduledTo,
  onConfirm,
  size = 'md',
}: Props) => {
  const { px, py, fontSize } = SIZES[size];
  // `inherit`, not `base`. `base` is not a line-height token in this theme and
  // is dropped as invalid CSS — which is what the old hand-rolled pill relied on
  // without knowing it, since the package `Text` inside it asked for `base` too
  // and fell back to the inherited value. Inheriting explicitly reproduces the
  // same 15px and keeps the pill at its original height.
  const chrome = { px, py, fontSize, minH: 0, lineHeight: 'inherit' } as const;

  // No pill: `occurred` is the resting state and reads as a caption, not a badge.
  if (status === 'occurred') {
    return (
      <Text
        color="bluegrey"
        fontWeight={500}
        fontSize={fontSize}
        whiteSpace="nowrap"
      >
        Occurred
      </Text>
    );
  }

  const entry = STATUS_CHIP[status];
  if (!entry) return null;

  // A control, not a label — the click confirms the appointment.
  if (status === 'unconfirmed' && onConfirm) {
    return (
      <Box
        as="span"
        onClick={onConfirm}
        cursor="pointer"
        title="Click to confirm"
        display="inline-flex"
      >
        <Chip variant={entry.variant} {...chrome}>
          {entry.label}
        </Chip>
      </Box>
    );
  }

  return (
    <Chip variant={entry.variant} {...chrome}>
      {status === 'rescheduled' && rescheduledTo ? (
        <>
          {entry.label}
          {/* fontSize and lineHeight are passed, not inherited: the package
              `Text` defaults to `sm` (14px), which inside a 12px pill grew the
              label's line box and made this the only status 3px taller than
              its siblings. The hand-rolled version passed the size through for
              the same reason. */}
          <Text
            as="span"
            color="bluegrey"
            fontWeight={400}
            fontSize={fontSize}
            lineHeight="inherit"
          >
            {' '}
            · {fmtSlot(rescheduledTo)}
          </Text>
        </>
      ) : (
        entry.label
      )}
    </Chip>
  );
};
